import React, { useState } from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  Label,
  Row,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap'
import { SelectedMCC, BusinessTypeWithCategory } from '../../types'
import { getFilteredBusinessTypes } from '../../utils'
import { useMCCData } from '../../hooks'
import MCCSearchInput from './components/mcc-search-input.component'
import MCCErrorDisplay from './components/mcc-error-display.component'
import MCCDropdownItem from './components/mcc-dropdown-item.component'

interface MCCSelectorProps {
  selectedMCC: SelectedMCC
  onMCCChange: (mcc: SelectedMCC) => void
  disabled?: boolean
}

const MCCSelector: React.FC<MCCSelectorProps> = ({
  selectedMCC,
  onMCCChange,
  disabled = false,
}) => {
  const {
    mccCategories,
    isLoading: isLoadingMCC,
    error: mccError,
    retry,
  } = useMCCData()
  const [mccDropdownOpen, setMccDropdownOpen] = useState(false)
  const [mccSearchTerm, setMccSearchTerm] = useState('')

  const handleMCCSelection = (
    businessType: BusinessTypeWithCategory,
    categoryName: string,
  ) => {
    onMCCChange({
      code: businessType.mcc,
      name: businessType.name,
      categoryName: categoryName,
    })
    setMccDropdownOpen(false)
    setMccSearchTerm('')
  }

  const filteredBusinessTypes = getFilteredBusinessTypes(
    mccCategories,
    mccSearchTerm,
  )

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="h5 mb-0">Merchant Category Code (MCC)</CardTitle>
      </CardHeader>
      <CardBody>
        <Form>
          <Row>
            <Col md={12}>
              <div className="mb-3">
                <Label className="form-label">
                  Business Type <span className="text-danger">*</span>
                </Label>
                <Dropdown
                  isOpen={mccDropdownOpen}
                  toggle={() => setMccDropdownOpen(!mccDropdownOpen)}
                  disabled={disabled || isLoadingMCC}
                >
                  <DropdownToggle
                    caret
                    className="w-100 text-start d-flex justify-content-between align-items-center"
                    color="light"
                    style={{ minHeight: '38px' }}
                  >
                    {selectedMCC.code ? (
                      <span>
                        <strong>{selectedMCC.name}</strong>
                        <small className="text-muted ms-2">
                          (MCC: {selectedMCC.code}) - {selectedMCC.categoryName}
                        </small>
                      </span>
                    ) : (
                      <span className="text-muted">
                        {isLoadingMCC
                          ? 'Loading business types...'
                          : mccError
                            ? 'Error loading business types'
                            : 'Select a business type'}
                      </span>
                    )}
                  </DropdownToggle>
                  <DropdownMenu
                    className="w-100"
                    style={{ maxHeight: '300px', overflowY: 'auto' }}
                  >
                    {mccError ? (
                      <MCCErrorDisplay
                        error={mccError}
                        isLoading={isLoadingMCC}
                        onRetry={retry}
                      />
                    ) : (
                      <>
                        <MCCSearchInput
                          searchTerm={mccSearchTerm}
                          onSearchChange={setMccSearchTerm}
                          disabled={isLoadingMCC}
                        />
                        {isLoadingMCC ? (
                          <DropdownItem disabled>
                            <div className="text-center py-2">
                              <i className="bx bx-loader bx-spin me-2"></i>
                              Loading business types...
                            </div>
                          </DropdownItem>
                        ) : filteredBusinessTypes.length === 0 ? (
                          <DropdownItem disabled>
                            {mccSearchTerm
                              ? 'No matching business types found'
                              : 'No business types available'}
                          </DropdownItem>
                        ) : (
                          filteredBusinessTypes.map((bt) => (
                            <MCCDropdownItem
                              key={`${bt.categoryName}-${bt.mcc}-${bt.name}`}
                              businessType={bt}
                              onSelect={handleMCCSelection}
                            />
                          ))
                        )}
                      </>
                    )}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  )
}

export default MCCSelector
